import Info from '@sats-group/icons/16/info';
import SvgLocation from '@sats-group/icons/18/location';
import SvgMail from '@sats-group/icons/24/mail';
import SvgPhone from '@sats-group/icons/24/phone';
import AvatarIcon from '@sats-group/icons/160/avatar-subtract';
import Button from '@sats-group/ui-lib/react/button';
import LinkButton from '@sats-group/ui-lib/react/link-button';
import Tag from '@sats-group/ui-lib/react/tag/tag';
import Text from '@sats-group/ui-lib/react/text';
import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import BootCampEventHero from '~/client/components/boot-camp-event-hero/boot-camp-event-hero';
import Breadcrumbs from '~/client/components/breadcrumbs/breadcrumbs';
import CleanLink from '~/client/components/clean-link/clean-link';
import ContentContainer from '~/client/components/content-container/content-container';
import DisplayBootCampEvents from '~/client/components/display-boot-camp-events/display-boot-camp-events';
import DynamicImage from '~/client/components/dynamic-image/dynamic-image';
import Layout from '~/client/components/layout/layout';
import type { BootCampEventPage as Props } from './boot-camp-event-page.types';

const BootCampEventPage: React.FC<Props> = ({
  about,
  actionBar,
  breadcrumbs,
  dates,
  hero,
  instructor,
  layout,
  others,
}) => {
  const ctaRef = useRef<HTMLDivElement>(null);
  const [isActionBarVisible, setIsActionBarVisible] = useState(false);

  useEffect(() => {
    if (!ctaRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsActionBarVisible(!(entry && entry.isIntersecting)),
      { threshold: 0 }
    );

    observer.observe(ctaRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <Layout {...layout}>
      <div className="boot-camp-event-page">
        <Breadcrumbs {...breadcrumbs} />
        <BootCampEventHero {...hero} actionRef={ctaRef} />
        <section className="boot-camp-event-page__section boot-camp-event-page__section--white">
          <ContentContainer
            theme={ContentContainer.themes.wide}
            className="boot-camp-event-page__section-inner"
          >
            <div className="boot-camp-event-page__split">
              <div className="boot-camp-event-page__col">
                <Text
                  elementName="h3"
                  size={Text.sizes.headline3}
                  theme={Text.themes.emphasis}
                  className="boot-camp-event-page__section-heading"
                >
                  {dates.heading}
                </Text>

                <ul className="boot-camp-event-page__dates-list">
                  {dates.times.map(entry => (
                    <li
                      key={entry.date}
                      className="boot-camp-event-page__date-row"
                    >
                      <div className="boot-camp-event-page__date-info">
                        <Text className="boot-camp-event-page__date-counter">
                          {entry.label}
                        </Text>
                        <Text
                          size={Text.sizes.basic}
                          className="boot-camp-event-page__date"
                        >
                          {entry.date}
                        </Text>
                      </div>
                      <Text className="boot-camp-event-page__time">
                        {entry.time}
                      </Text>
                    </li>
                  ))}
                </ul>
                <div className="boot-camp-event-page__session-info">
                  <Info />
                  <Text size={Text.sizes.small}>{dates.sessionDuration}</Text>
                </div>
              </div>
              <div className="boot-camp-event-page__col">
                {about.map((section, index) => (
                  <div key={index} className="boot-camp-event-page__about">
                    <Text
                      elementName="h3"
                      theme={Text.themes.emphasis}
                      size={Text.sizes.headline3}
                      className="boot-camp-event-page__section-heading"
                    >
                      {section.heading}
                    </Text>
                    <Text>{section.text}</Text>
                  </div>
                ))}
              </div>
            </div>
          </ContentContainer>
        </section>

        {instructor ? (
          <section className="boot-camp-event-page__section">
            <ContentContainer
              theme={ContentContainer.themes.wide}
              className="boot-camp-event-page__section-inner boot-camp-event-page__section-inner--roomier"
            >
              <div className="boot-camp-event-page__personal-trainer">
                <div className="boot-camp-event-page__image">
                  {instructor.personalTrainer.image ? (
                    <DynamicImage
                      {...instructor.personalTrainer.image}
                      aspectRatio={DynamicImage.aspectRatios.square}
                    />
                  ) : (
                    <AvatarIcon />
                  )}
                </div>
                <div className="boot-camp-event-page__meta-container">
                  <div>
                    <Text
                      elementName="p"
                      className="boot-camp-event-page__pt-tagline"
                    >
                      {instructor.tagline}
                    </Text>
                    <Text
                      elementName="h2"
                      theme={Text.themes.emphasis}
                      size={Text.sizes.headline1}
                    >
                      {instructor.personalTrainer.name}
                    </Text>
                    <div className="boot-camp-event-page__meta">
                      {instructor.personalTrainer.level ? (
                        <Tag
                          {...instructor.personalTrainer.level}
                          variant={Tag.variants.tertiary}
                        />
                      ) : null}
                      <Tag
                        {...instructor.personalTrainer.clubName}
                        icon={<SvgLocation />}
                        size={Tag.sizes.s}
                        variant={Tag.variants.primary}
                        border
                      />
                    </div>
                  </div>
                </div>

                {instructor.personalTrainer.profileUrl ? (
                  <div className="boot-camp-event-page__profile-link">
                    <LinkButton
                      variant={LinkButton.variants.secondary}
                      size={LinkButton.sizes.small}
                      {...instructor.personalTrainer.profileUrl}
                    />
                  </div>
                ) : null}

                <div className="boot-camp-event-page__bio">
                  <Text>{instructor.personalTrainer.description}</Text>
                </div>

                <div className="boot-camp-event-page__contact">
                  <Text elementName="p" size={Text.sizes.small}>
                    {instructor.text}
                  </Text>
                  <div className="boot-camp-event-page__contact-items">
                    {instructor.personalTrainer.phoneNumber ? (
                      <div className="boot-camp-event-page__contact-item">
                        <div className="boot-camp-event-page__contact-icon">
                          <SvgPhone />
                        </div>
                        <div>
                          <Text>
                            {instructor.personalTrainer.phoneNumber.label}
                          </Text>
                          <Text theme={Text.themes.emphasis}>
                            {instructor.personalTrainer.phoneNumber.value}
                          </Text>
                        </div>
                      </div>
                    ) : null}
                    <div className="boot-camp-event-page__contact-item">
                      <div className="boot-camp-event-page__contact-icon">
                        <SvgMail />
                      </div>
                      <div>
                        <Text>{instructor.personalTrainer.email.label}</Text>
                        <CleanLink
                          {...instructor.personalTrainer.email.link}
                          theme={CleanLink.themes.blue}
                          bold
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ContentContainer>
          </section>
        ) : null}

        {others ? (
          <section className="boot-camp-event-page__section boot-camp-event-page__section--white">
            <ContentContainer
              theme={ContentContainer.themes.wide}
              className="boot-camp-event-page__section-inner"
            >
              <Text
                elementName="h3"
                size={Text.sizes.headline3}
                theme={Text.themes.emphasis}
                className="boot-camp-event-page__section-heading"
              >
                {others.title}
              </Text>
              <Text>{others.blurb}</Text>
              <DisplayBootCampEvents {...others.entries} />
            </ContentContainer>
          </section>
        ) : null}
      </div>
      <section
        className={cn('boot-camp-event-page__action', {
          'boot-camp-event-page__action--visible': isActionBarVisible,
        })}
      >
        <ContentContainer theme={ContentContainer.themes.wide}>
          <div className="boot-camp-event-page__action-inner">
            <div>
              {actionBar.prices.map(price => (
                <div
                  key={price.price}
                  className="boot-camp-event-page__action-pricing"
                >
                  <Text elementName="span">{price.text}</Text>
                  <Text elementName="span" theme={Text.themes.emphasis}>
                    {price.price}
                  </Text>
                </div>
              ))}
            </div>
            <LinkButton {...actionBar.cta} variant={Button.variants.cta} />
          </div>
        </ContentContainer>
      </section>
    </Layout>
  );
};

export default BootCampEventPage;
